import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Wrench, Calendar, Plus, X, CheckCircle2, AlertCircle, ClipboardList,
  Loader2, Trash2, FileText, PoundSterling, Clock, Settings2, BellRing,
  ImageIcon, Camera, Paperclip, Download, Mail,
} from "lucide-react";
import { api, formatApiError, API } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { GBP, fmtDateGB } from "../../lib/format";
import DateInput from "../../components/DateInput";

const fmtGbp = (v) => GBP.format(Number(v) || 0);

/**
 * Project Detail → Maintenance tab.
 *
 * Renders the full service history for the project, a booking flow for
 * new visits, an editor for logging findings/parts/labour on completion,
 * and the annual-PM reminder config panel that drives the background
 * reminder loop.
 */

const STATUS_TONE = {
  draft: "warn",
  scheduled: "ink",
  in_progress: "signal",
  completed: "ok",
  cancelled: "muted",
};
const STATUS_LABEL = {
  draft: "Draft",
  scheduled: "Scheduled",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

const OUTCOME_TONE = { passed: "ok", failed: "danger", follow_up: "warn" };
const OUTCOME_LABEL = { passed: "Passed", failed: "Failed", follow_up: "Follow-up" };

export default function MaintenanceTab({ project, reload }) {
  const { user } = useAuth();
  const canEdit = ["admin", "manager", "epc", "contractor"].includes(user?.role);
  const canDelete = ["admin", "manager"].includes(user?.role);

  const [config, setConfig] = useState(null);
  const [nextDue, setNextDue] = useState(null);
  const [types, setTypes] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [visits, setVisits] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visitModal, setVisitModal] = useState(null); // {mode, data}
  const [configModal, setConfigModal] = useState(false);
  const [reportModal, setReportModal] = useState(false);

  const pid = project?.id;

  const load = async () => {
    if (!pid) return;
    setLoading(true);
    try {
      const [cfg, ts, chk, vs, usrs] = await Promise.all([
        api.get(`/projects/${pid}/maintenance/config`),
        api.get(`/maintenance/visit-types`),
        api.get(`/maintenance/findings-checklist`),
        api.get(`/projects/${pid}/maintenance/visits`),
        api.get(`/users`).catch(() => ({ data: [] })),
      ]);
      setConfig(cfg.data.config);
      setNextDue(cfg.data.next_due_date);
      setTypes((ts.data || []).filter(t => t.active !== false));
      setChecklist(chk.data || []);
      setVisits(vs.data || []);
      setUsers((usrs.data || []).filter(u => ["admin", "manager", "epc", "contractor", "asset_manager"].includes(u.role)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load maintenance data");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [pid]);

  const upcoming = useMemo(
    () => visits.filter(v => !["completed", "cancelled"].includes(v.status)),
    [visits]
  );
  const history = useMemo(
    () => visits.filter(v => v.status === "completed"),
    [visits]
  );

  const totals = useMemo(() => {
    let totalCost = 0, totalLabourHours = 0;
    for (const v of history) {
      const parts = (v.parts_used || []).reduce((n, p) => n + (Number(p.qty) || 0) * (Number(p.unit_cost_gbp) || 0), 0);
      const labour = (Number(v.labour_hours) || 0) * (Number(v.labour_rate_gbp) || 0);
      totalCost += parts + labour;
      totalLabourHours += Number(v.labour_hours) || 0;
    }
    return { totalCost, totalLabourHours };
  }, [history]);

  const daysUntil = (iso) => {
    if (!iso) return null;
    const d = Math.round((new Date(iso).getTime() - Date.now()) / 86400000);
    return d;
  };

  if (loading) return (
    <div className="p-8 text-center muted"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading…</div>
  );

  return (
    <div className="space-y-6" data-testid="maintenance-tab">
      {/* Reminder banner */}
      <ReminderBanner
        nextDue={nextDue} config={config}
        onConfigure={() => setConfigModal(true)}
      />

      {/* Header + stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Visits total" value={visits.length} icon={<Calendar size={13} />} />
        <StatTile label="Completed" value={history.length} icon={<CheckCircle2 size={13} />} />
        <StatTile label="Total spend" value={fmtGbp(totals.totalCost)} icon={<PoundSterling size={13} />} />
        <StatTile label="Labour hours" value={totals.totalLabourHours.toFixed(1)} icon={<Clock size={13} />} />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-xl font-semibold flex items-center gap-2">
          <Wrench size={16} style={{ color: "var(--accent-hover)" }} /> Upcoming visits
        </h3>
        <div className="flex gap-2">
          <button className="btn-ghost" onClick={() => setConfigModal(true)} data-testid="maint-config-btn">
            <Settings2 size={13} /> Reminder config
          </button>
          {canEdit && (
            <button className="btn-ghost" onClick={() => setReportModal(true)} data-testid="maint-report-btn">
              <FileText size={13} /> Ops report
            </button>
          )}
          {canEdit && (
            <button className="btn-accent" onClick={() => setVisitModal({
              mode: "create",
              data: { visit_type_key: types[0]?.key || "annual_pm", scheduled_date: new Date().toISOString().slice(0, 10), technician_id: "", notes: "" },
            })} data-testid="maint-book-btn">
              <Plus size={13} /> Book visit
            </button>
          )}
        </div>
      </div>

      {/* Upcoming */}
      {upcoming.length === 0 ? (
        <EmptyState msg="No upcoming visits." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {upcoming.map((v) => (
            <VisitCard key={v.id} v={v} daysUntil={daysUntil(v.scheduled_date)}
                       onOpen={() => setVisitModal({ mode: "edit", data: { ...v } })}
                       canEdit={canEdit} />
          ))}
        </div>
      )}

      {/* Service history */}
      <div className="mt-8">
        <h3 className="font-display text-xl font-semibold flex items-center gap-2 mb-3">
          <ClipboardList size={16} style={{ color: "var(--accent-hover)" }} /> Service history
        </h3>
        {history.length === 0 ? (
          <EmptyState msg="No completed visits yet." />
        ) : (
          <div className="border border-[var(--line)] rounded-md overflow-hidden">
            <table className="data-table w-full" data-testid="maint-history-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Technician</th>
                  <th>Outcome</th>
                  <th className="text-right">Labour</th>
                  <th className="text-right">Cost</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {history.map((v) => {
                  const parts = (v.parts_used || []).reduce((n, p) => n + (Number(p.qty) || 0) * (Number(p.unit_cost_gbp) || 0), 0);
                  const labour = (Number(v.labour_hours) || 0) * (Number(v.labour_rate_gbp) || 0);
                  return (
                    <tr key={v.id} data-testid={`maint-visit-row-${v.id}`}>
                      <td className="font-mono text-xs">{v.completed_date || v.scheduled_date}</td>
                      <td>{v.visit_type_label}</td>
                      <td className="muted text-xs">{v.technician_name || "—"}</td>
                      <td>
                        {v.outcome ? (
                          <span className={`tag ${OUTCOME_TONE[v.outcome]} text-[10px]`}>{OUTCOME_LABEL[v.outcome]}</span>
                        ) : "—"}
                      </td>
                      <td className="text-right font-mono text-xs">{(Number(v.labour_hours) || 0).toFixed(1)}h</td>
                      <td className="text-right font-mono">{fmtGbp(parts + labour)}</td>
                      <td className="num">
                        <button onClick={() => setVisitModal({ mode: "edit", data: { ...v } })}
                                data-testid={`maint-view-${v.id}`}>
                          <FileText size={13} className="muted" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {visitModal && (
        <VisitModal
          mode={visitModal.mode}
          initial={visitModal.data}
          types={types}
          checklist={checklist}
          users={users}
          canEdit={canEdit}
          canDelete={canDelete}
          projectId={pid}
          onClose={() => setVisitModal(null)}
          onSaved={() => { setVisitModal(null); load(); reload && reload(); }}
        />
      )}
      {configModal && (
        <ConfigModal
          initial={config || {}} nextDue={nextDue}
          handoverDate={project?.handover_date}
          installationDate={project?.installation_date}
          users={users}
          projectId={pid}
          onClose={() => setConfigModal(false)}
          onSaved={() => { setConfigModal(false); load(); reload && reload(); }}
        />
      )}
      {reportModal && (
        <ReportModal
          projectId={pid}
          projectName={project?.name}
          config={config || {}}
          onClose={() => setReportModal(false)}
        />
      )}
    </div>
  );
}


function StatTile({ label, value, icon }) {
  return (
    <div className="swiss-card-flat p-3">
      <div className="label mb-1 flex items-center gap-1.5">{icon}{label}</div>
      <div className="font-mono text-lg font-semibold">{value}</div>
    </div>
  );
}


function ReminderBanner({ nextDue, config, onConfigure }) {
  if (!config?.active) {
    return (
      <div className="swiss-card-flat p-4 border-l-2 border-[var(--muted-line,#d0d0d0)]" data-testid="maint-reminder-off">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="label mb-1 flex items-center gap-1.5"><BellRing size={13} /> Annual reminders</div>
            <p className="text-sm muted leading-relaxed">
              Annual maintenance reminders are off for this site. Turn them on to auto-queue a draft visit 30 days ahead and email the technician + Asset Manager at 30-day and 7-day marks.
            </p>
          </div>
          <button className="btn-accent shrink-0" onClick={onConfigure} data-testid="maint-reminder-enable">
            <Settings2 size={13} /> Configure
          </button>
        </div>
      </div>
    );
  }
  const d = nextDue ? Math.round((new Date(nextDue).getTime() - Date.now()) / 86400000) : null;
  const tone = d == null ? "muted" : d < 7 ? "danger" : d < 30 ? "warn" : "ok";
  const toneColor = {
    ok: "#00D6DF", warn: "#B33C86", danger: "#D64545", muted: "var(--muted-line, #ccc)",
  }[tone];
  return (
    <div className="swiss-card-flat p-4" style={{ borderLeft: `3px solid ${toneColor}` }} data-testid="maint-reminder-on">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="label mb-1 flex items-center gap-1.5" style={{ color: toneColor }}>
            <BellRing size={13} /> Annual reminders active
          </div>
          {nextDue ? (
            <p className="text-sm leading-relaxed">
              Next visit due <strong>{nextDue}</strong>
              {d != null && <span className="muted"> — {d >= 0 ? `in ${d} days` : `${Math.abs(d)} days overdue`}</span>}.
              Anchor: <span className="font-mono text-xs">{config.annual_anchor}</span>, every {config.cadence_months || 12} months.
            </p>
          ) : (
            <p className="text-sm leading-relaxed muted">
              Reminders are on but no anchor date resolved — set a handover/installation date on the project or pick a custom next-due date.
            </p>
          )}
        </div>
        <button className="btn-ghost shrink-0" onClick={onConfigure} data-testid="maint-reminder-edit">
          <Settings2 size={13} /> Edit
        </button>
      </div>
    </div>
  );
}


function VisitCard({ v, daysUntil, onOpen, canEdit }) {
  const tone = STATUS_TONE[v.status] || "ink";
  const stale = daysUntil != null && daysUntil < 0 && v.status !== "completed";
  return (
    <button onClick={onOpen} className="swiss-card-flat p-4 text-left w-full hover:border-[var(--accent)] transition-colors"
            data-testid={`maint-visit-card-${v.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`tag ${tone} text-[10px]`}>{STATUS_LABEL[v.status] || v.status}</span>
            {v.source === "auto" && <span className="tag text-[10px] muted">auto</span>}
            {stale && <span className="tag danger text-[10px]">overdue</span>}
          </div>
          <div className="font-medium text-sm mb-1">{v.visit_type_label}</div>
          <div className="text-xs muted flex items-center gap-1.5">
            <Calendar size={11} /> {fmtDateGB(v.scheduled_date)}
            {daysUntil != null && (
              <span className="ml-1">· {daysUntil >= 0 ? `in ${daysUntil}d` : `${Math.abs(daysUntil)}d overdue`}</span>
            )}
          </div>
          {v.technician_name && (
            <div className="text-xs muted mt-1">Technician: {v.technician_name}</div>
          )}
        </div>
        {canEdit && <FileText size={13} className="muted shrink-0" />}
      </div>
      {v.notes && <div className="text-xs muted mt-2 line-clamp-2">{v.notes}</div>}
    </button>
  );
}


function EmptyState({ msg }) {
  return (
    <div className="swiss-card-flat p-6 text-center muted text-sm italic" data-testid="maint-empty">
      {msg}
    </div>
  );
}


// ============================================================
// Visit modal (create + edit + complete)
// ============================================================
function VisitModal({ mode, initial, types, checklist, users, canEdit, canDelete, projectId, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    findings: {}, parts_used: [], ...initial,
  }));
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("details");

  const upd = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const setFinding = (key, val) => setForm(f => ({ ...f, findings: { ...(f.findings || {}), [key]: val } }));

  const addPart = () => setForm(f => ({ ...f, parts_used: [...(f.parts_used || []), { name: "", qty: 1, unit_cost_gbp: 0 }] }));
  const updPart = (idx, k, v) => setForm(f => ({
    ...f,
    parts_used: (f.parts_used || []).map((p, i) => i === idx ? { ...p, [k]: v } : p),
  }));
  const removePart = (idx) => setForm(f => ({ ...f, parts_used: (f.parts_used || []).filter((_, i) => i !== idx) }));

  const partsTotal = useMemo(
    () => (form.parts_used || []).reduce((n, p) => n + (Number(p.qty) || 0) * (Number(p.unit_cost_gbp) || 0), 0),
    [form.parts_used]
  );
  const labourTotal = (Number(form.labour_hours) || 0) * (Number(form.labour_rate_gbp) || 0);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "create") {
        await api.post(`/projects/${projectId}/maintenance/visits`, {
          visit_type_key: form.visit_type_key,
          scheduled_date: form.scheduled_date,
          technician_id: form.technician_id || null,
          notes: form.notes || null,
        });
        toast.success("Visit booked");
      } else {
        const payload = {
          visit_type_key: form.visit_type_key,
          scheduled_date: form.scheduled_date,
          completed_date: form.completed_date || null,
          technician_id: form.technician_id || null,
          status: form.status,
          outcome: form.outcome || null,
          notes: form.notes || null,
          findings: form.findings || {},
          parts_used: (form.parts_used || []).map(p => ({
            name: p.name, qty: Number(p.qty) || 0, unit_cost_gbp: Number(p.unit_cost_gbp) || 0, note: p.note || null,
          })),
          labour_hours: form.labour_hours != null && form.labour_hours !== "" ? Number(form.labour_hours) : null,
          labour_rate_gbp: form.labour_rate_gbp != null && form.labour_rate_gbp !== "" ? Number(form.labour_rate_gbp) : null,
        };
        await api.patch(`/projects/${projectId}/maintenance/visits/${form.id}`, payload);
        toast.success("Visit updated");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm(`Delete this visit? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/projects/${projectId}/maintenance/visits/${form.id}`);
      toast.success("Visit deleted"); onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="maint-visit-modal">
      <form className="modal-card p-0 max-w-3xl w-full" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        {/* Header */}
        <div className="px-6 py-5 border-b border-[var(--line)]">
          <div className="flex items-center justify-between">
            <div>
              <div className="label">{mode === "create" ? "Book" : "Manage"}</div>
              <div className="font-display text-xl font-semibold mt-1">
                {mode === "create" ? "New maintenance visit" : (form.visit_type_label || "Visit")}
              </div>
            </div>
            <button type="button" onClick={onClose} className="muted" data-testid="maint-visit-close"><X size={18} /></button>
          </div>
        </div>

        {/* Tabs */}
        {mode === "edit" && (
          <div className="border-b border-[var(--line)] flex gap-1 px-4">
            {[
              { k: "details", l: "Details" },
              { k: "findings", l: "Findings" },
              { k: "parts", l: "Parts & Labour" },
              { k: "photos", l: "Photos" },
            ].map(t => (
              <button type="button" key={t.k}
                onClick={() => setTab(t.k)}
                className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider ${tab === t.k ? "border-b-2 border-[var(--accent)] text-[var(--accent-hover)]" : "muted"}`}
                data-testid={`maint-visit-tab-${t.k}`}>
                {t.l}
              </button>
            ))}
          </div>
        )}

        <div className="p-6 max-h-[65vh] overflow-y-auto">
          {(mode === "create" || tab === "details") && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block">
                <span className="label block mb-1">Visit type</span>
                <select className="input-swiss" value={form.visit_type_key || ""} onChange={upd("visit_type_key")} data-testid="maint-visit-type">
                  {types.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="label block mb-1">Scheduled date</span>
                <DateInput className="input-swiss" value={form.scheduled_date || ""} onChange={upd("scheduled_date")} data-testid="maint-visit-date" required />
              </label>
              <label className="block">
                <span className="label block mb-1">Technician</span>
                <select className="input-swiss" value={form.technician_id || ""} onChange={upd("technician_id")} data-testid="maint-visit-tech">
                  <option value="">— unassigned —</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name || u.email} ({u.role})</option>)}
                </select>
              </label>
              {mode === "edit" && (
                <>
                  <label className="block">
                    <span className="label block mb-1">Status</span>
                    <select className="input-swiss" value={form.status || "scheduled"} onChange={upd("status")} data-testid="maint-visit-status">
                      <option value="draft">Draft</option>
                      <option value="scheduled">Scheduled</option>
                      <option value="in_progress">In progress</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="label block mb-1">Outcome</span>
                    <select className="input-swiss" value={form.outcome || ""} onChange={upd("outcome")} data-testid="maint-visit-outcome">
                      <option value="">—</option>
                      <option value="passed">Passed</option>
                      <option value="failed">Failed</option>
                      <option value="follow_up">Follow-up</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="label block mb-1">Completed date</span>
                    <DateInput className="input-swiss" value={form.completed_date || ""} onChange={upd("completed_date")} data-testid="maint-visit-completed" />
                  </label>
                </>
              )}
              <label className="block md:col-span-2">
                <span className="label block mb-1">Notes</span>
                <textarea className="input-swiss min-h-[80px]" value={form.notes || ""} onChange={upd("notes")} data-testid="maint-visit-notes" placeholder="Site notes, access instructions, etc." />
              </label>
            </div>
          )}

          {mode === "edit" && tab === "findings" && (
            <div className="space-y-3" data-testid="maint-findings-panel">
              <div className="text-xs muted mb-2">
                Complete the site checklist. Fields tagged as pass/fail take pass · fail · N/A; free-text ones accept any observation.
              </div>
              {checklist.map(item => (
                <div key={item.key} className="grid grid-cols-3 items-center gap-3 py-2 border-b border-[var(--line)] last:border-0">
                  <div className="text-sm">{item.label}</div>
                  {item.type === "pass_fail" ? (
                    <div className="col-span-2 flex gap-2">
                      {["pass", "fail", "na"].map(opt => (
                        <button type="button" key={opt}
                          onClick={() => setFinding(item.key, opt)}
                          className={`px-3 py-1 text-xs rounded ${form.findings?.[item.key] === opt ? "bg-[var(--accent-soft)] text-[var(--accent-hover)] border border-[var(--accent)]" : "border border-[var(--line)] muted hover:border-[var(--accent)]"}`}
                          data-testid={`maint-finding-${item.key}-${opt}`}>
                          {opt === "na" ? "N/A" : opt}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <input className="input-swiss col-span-2" value={form.findings?.[item.key] || ""} onChange={(e) => setFinding(item.key, e.target.value)}
                           data-testid={`maint-finding-${item.key}`} />
                  )}
                </div>
              ))}
            </div>
          )}

          {mode === "edit" && tab === "parts" && (
            <div data-testid="maint-parts-panel">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <label className="block">
                  <span className="label block mb-1">Labour hours</span>
                  <input type="number" step="0.25" min="0" className="input-swiss" value={form.labour_hours ?? ""} onChange={upd("labour_hours")} data-testid="maint-labour-hours" />
                </label>
                <label className="block">
                  <span className="label block mb-1">Labour rate (£/hr)</span>
                  <input type="number" step="0.5" min="0" className="input-swiss" value={form.labour_rate_gbp ?? ""} onChange={upd("labour_rate_gbp")} data-testid="maint-labour-rate" />
                </label>
              </div>

              <div className="flex items-center justify-between mb-2">
                <div className="label">Parts used</div>
                <button type="button" className="btn-ghost text-xs" onClick={addPart} data-testid="maint-part-add">
                  <Plus size={12} /> Add part
                </button>
              </div>
              <div className="space-y-2" data-testid="maint-parts-list">
                {(form.parts_used || []).length === 0 && (
                  <div className="text-xs muted italic">No parts logged.</div>
                )}
                {(form.parts_used || []).map((p, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <input className="input-swiss col-span-5" placeholder="Part name" value={p.name} onChange={(e) => updPart(i, "name", e.target.value)} data-testid={`maint-part-name-${i}`} />
                    <input type="number" step="0.5" min="0" className="input-swiss col-span-2" placeholder="Qty" value={p.qty} onChange={(e) => updPart(i, "qty", e.target.value)} data-testid={`maint-part-qty-${i}`} />
                    <input type="number" step="0.01" min="0" className="input-swiss col-span-3" placeholder="£/unit" value={p.unit_cost_gbp} onChange={(e) => updPart(i, "unit_cost_gbp", e.target.value)} data-testid={`maint-part-cost-${i}`} />
                    <div className="col-span-1 font-mono text-xs text-right">{fmtGbp((Number(p.qty) || 0) * (Number(p.unit_cost_gbp) || 0))}</div>
                    <button type="button" className="col-span-1" onClick={() => removePart(i)} data-testid={`maint-part-del-${i}`}><Trash2 size={12} className="muted" /></button>
                  </div>
                ))}
              </div>

              <div className="mt-4 p-3 bg-[var(--bg-2)] rounded border border-[var(--line)]">
                <div className="flex justify-between text-xs mb-1"><span className="muted">Parts subtotal</span><span className="font-mono">{fmtGbp(partsTotal)}</span></div>
                <div className="flex justify-between text-xs mb-1"><span className="muted">Labour</span><span className="font-mono">{fmtGbp(labourTotal)}</span></div>
                <div className="flex justify-between text-sm font-semibold pt-1 border-t border-[var(--line)]"><span>Visit total</span><span className="font-mono" data-testid="maint-visit-total">{fmtGbp(partsTotal + labourTotal)}</span></div>
              </div>
            </div>
          )}

          {mode === "edit" && tab === "photos" && (
            <PhotosPanel
              projectId={projectId} visitId={form.id}
              photos={form.photos || []}
              findings={checklist}
              onChange={(next) => setForm(f => ({ ...f, photos: next }))}
              canEdit={canEdit}
            />
          )}
        </div>

        <div className="px-6 py-4 border-t border-[var(--line)] flex justify-between">
          <div>
            {mode === "edit" && canDelete && (
              <button type="button" onClick={del} disabled={busy} className="btn-ghost text-[var(--danger,#D64545)]" data-testid="maint-visit-delete">
                <Trash2 size={12} /> Delete
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" className="btn-ghost" onClick={onClose} data-testid="maint-visit-cancel">Cancel</button>
            {canEdit && (
              <button type="submit" disabled={busy} className="btn-accent" data-testid="maint-visit-save">
                {busy ? <Loader2 size={12} className="animate-spin inline mr-1" /> : null}
                {mode === "create" ? "Book visit" : "Save changes"}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}


// ============================================================
// Reminder config modal
// ============================================================
function ConfigModal({ initial, nextDue, handoverDate, installationDate, users, projectId, onClose, onSaved }) {
  const [form, setForm] = useState({
    active: initial.active || false,
    annual_anchor: initial.annual_anchor || "handover_date",
    custom_next_due: initial.custom_next_due || "",
    cadence_months: initial.cadence_months || 12,
    assigned_technician_id: initial.assigned_technician_id || "",
    notify_am: initial.notify_am !== false,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/projects/${projectId}/maintenance/config`, {
        ...form,
        assigned_technician_id: form.assigned_technician_id || null,
        custom_next_due: form.annual_anchor === "custom" ? (form.custom_next_due || null) : null,
      });
      toast.success("Reminder config saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="maint-config-modal">
      <form className="modal-card p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="label">Configure</div>
            <div className="font-display text-xl font-semibold mt-1">Annual maintenance reminders</div>
          </div>
          <button type="button" onClick={onClose} className="muted"><X size={18} /></button>
        </div>

        <div className="space-y-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm(f => ({ ...f, active: e.target.checked }))} data-testid="maint-cfg-active" />
            <span className="text-sm">Auto-remind + auto-queue a draft visit for annual PM</span>
          </label>

          <label className="block">
            <span className="label block mb-1">Anchor</span>
            <select className="input-swiss" value={form.annual_anchor} onChange={(e) => setForm(f => ({ ...f, annual_anchor: e.target.value }))} data-testid="maint-cfg-anchor">
              <option value="handover_date">Handover date {handoverDate ? `(${fmtDateGB(handoverDate)})` : "(not set)"}</option>
              <option value="installation_date">Installation date {installationDate ? `(${fmtDateGB(installationDate)})` : "(not set)"}</option>
              <option value="custom">Custom next-due override</option>
            </select>
            <div className="text-[11px] muted mt-1">Reminder cadence rolls forward from the anchor by the configured months.</div>
          </label>

          {form.annual_anchor === "custom" && (
            <label className="block">
              <span className="label block mb-1">Custom next-due date</span>
              <DateInput className="input-swiss" value={form.custom_next_due || ""} onChange={(e) => setForm(f => ({ ...f, custom_next_due: e.target.value }))} data-testid="maint-cfg-custom-date" />
            </label>
          )}

          <label className="block">
            <span className="label block mb-1">Cadence (months)</span>
            <input type="number" min="1" className="input-swiss" value={form.cadence_months} onChange={(e) => setForm(f => ({ ...f, cadence_months: Number(e.target.value) }))} data-testid="maint-cfg-cadence" />
          </label>

          <label className="block">
            <span className="label block mb-1">Default technician (optional)</span>
            <select className="input-swiss" value={form.assigned_technician_id} onChange={(e) => setForm(f => ({ ...f, assigned_technician_id: e.target.value }))} data-testid="maint-cfg-tech">
              <option value="">— none —</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name || u.email} ({u.role})</option>)}
            </select>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.notify_am} onChange={(e) => setForm(f => ({ ...f, notify_am: e.target.checked }))} data-testid="maint-cfg-notify-am" />
            <span className="text-sm">Also notify the Asset Manager on reminders</span>
          </label>

          {nextDue && form.active && (
            <div className="p-3 bg-[var(--accent-soft)] rounded text-xs" data-testid="maint-cfg-preview">
              <div className="label mb-1" style={{ color: "var(--accent-hover)" }}>Preview</div>
              Next visit would be due <strong>{nextDue}</strong>. A draft visit auto-appears 30 days beforehand.
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button type="button" className="btn-ghost" onClick={onClose} data-testid="maint-cfg-cancel">Cancel</button>
          <button type="submit" disabled={busy} className="btn-accent" data-testid="maint-cfg-save">
            {busy ? <Loader2 size={12} className="animate-spin inline mr-1" /> : null}
            Save
          </button>
        </div>
      </form>
    </div>
  );
}


// ============================================================
// Photos panel — drop-zone + gallery grid, optional finding tag
// ============================================================
function PhotosPanel({ projectId, visitId, photos, findings, onChange, canEdit }) {
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [annotate, setAnnotate] = useState(null); // photo obj

  const upload = async (fileList, findingKey = null) => {
    if (!fileList?.length) return;
    setBusy(true);
    try {
      for (const f of fileList) {
        if (!f.type.startsWith("image/")) { toast.error(`${f.name}: not an image`); continue; }
        if (f.size > 20 * 1024 * 1024) { toast.error(`${f.name}: exceeds 20MB`); continue; }
        const fd = new FormData();
        fd.append("file", f);
        fd.append("project_id", projectId);
        const { data: up } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const { data: photo } = await api.post(
          `/projects/${projectId}/maintenance/visits/${visitId}/photos`,
          { file_id: up.id, name: f.name, finding_key: findingKey || null }
        );
        onChange([...(photos || []), photo]);
      }
      toast.success(`${fileList.length} photo${fileList.length === 1 ? "" : "s"} uploaded`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  };

  const remove = async (photo) => {
    if (!window.confirm("Remove this photo from the visit?")) return;
    setBusy(true);
    try {
      await api.delete(`/projects/${projectId}/maintenance/visits/${visitId}/photos/${photo.file_id}`);
      onChange((photos || []).filter(p => p.file_id !== photo.file_id));
      toast.success("Photo removed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    } finally { setBusy(false); }
  };

  const grouped = useMemo(() => {
    const g = { _visit: [] };
    for (const p of photos || []) {
      const k = p.finding_key || "_visit";
      (g[k] = g[k] || []).push(p);
    }
    return g;
  }, [photos]);

  return (
    <div data-testid="maint-photos-panel">
      {canEdit && (
        <label
          className={`block border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors ${dragOver ? "border-[var(--accent)] bg-[var(--accent-soft)]" : "border-[var(--line)]"}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files); }}
          data-testid="maint-photo-dropzone"
        >
          <Camera size={22} className="inline muted mb-2" />
          <div className="text-sm"><span className="font-medium">Drop photos here</span> or click to browse</div>
          <div className="text-[11px] muted mt-1">JPG / PNG / WebP up to 20MB. Tag them to a finding row from the Findings tab.</div>
          <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload(e.target.files)} data-testid="maint-photo-input" />
        </label>
      )}
      {busy && <div className="text-center text-xs muted mt-3"><Loader2 size={12} className="animate-spin inline mr-1" /> Uploading…</div>}

      <PhotoGrid title="Visit photos" photos={grouped._visit || []} onRemove={remove} onAnnotate={setAnnotate} canEdit={canEdit} />
      {findings.map(f => (grouped[f.key] || []).length > 0 && (
        <PhotoGrid key={f.key} title={f.label} photos={grouped[f.key]} onRemove={remove} onAnnotate={setAnnotate} canEdit={canEdit} />
      ))}

      {(!photos || photos.length === 0) && !busy && (
        <div className="mt-6 text-center text-xs muted italic">No photos yet.</div>
      )}

      {annotate && (
        <AnnotationModal
          photo={annotate}
          projectId={projectId}
          visitId={visitId}
          onClose={() => setAnnotate(null)}
          onSaved={(newPhoto) => { onChange([...(photos || []), newPhoto]); setAnnotate(null); }}
        />
      )}
    </div>
  );
}

function PhotoGrid({ title, photos, onRemove, onAnnotate, canEdit }) {
  if (!photos.length) return null;
  return (
    <div className="mt-4">
      <div className="label mb-2">{title} <span className="muted">({photos.length})</span></div>
      <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
        {photos.map(p => (
          <div key={p.file_id} className="relative group border border-[var(--line)] rounded overflow-hidden" data-testid={`maint-photo-tile-${p.file_id}`}>
            <img src={`${API}/files/${p.file_id}`} alt={p.name || "photo"} className="w-full aspect-square object-cover" loading="lazy" />
            <div className="absolute inset-x-0 bottom-0 bg-black/70 px-2 py-1 text-white text-[10px] truncate">
              {p.name}
              {p.annotated_from && <span className="ml-1 text-[8px] uppercase tracking-wider text-[var(--warn,#B33C86)]">· annotated</span>}
            </div>
            {canEdit && !p.annotated_from && (
              <button type="button" onClick={(e) => { e.preventDefault(); onAnnotate(p); }}
                      className="absolute top-1 left-1 bg-white/95 rounded p-1 shadow opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`maint-photo-annotate-${p.file_id}`} title="Annotate">
                <Camera size={12} />
              </button>
            )}
            {canEdit && (
              <button type="button" onClick={(e) => { e.preventDefault(); onRemove(p); }}
                      className="absolute top-1 right-1 bg-white/95 rounded p-1 shadow opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`maint-photo-del-${p.file_id}`} title="Remove">
                <Trash2 size={12} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// ============================================================
// Monthly Ops Report modal — download PDF or email
// ============================================================
function ReportModal({ projectId, projectName, config, onClose }) {
  const now = new Date();
  const defaultMonth = (() => {
    const y = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    const m = now.getMonth() === 0 ? 12 : now.getMonth();
    return `${y}-${String(m).padStart(2, "0")}`;
  })();

  const [month, setMonth] = useState(defaultMonth);
  const [recipients, setRecipients] = useState((config.report_recipients || []).join(", "));
  const [busy, setBusy] = useState(false);
  const [reportActive, setReportActive] = useState(!!config.report_active);
  const [savingCfg, setSavingCfg] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const resp = await api.post(
        `/projects/${projectId}/maintenance/reports/monthly`,
        { month, send_email: false },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ops-report-${(projectName || "project").replace(/\s+/g, "_")}-${month.replace("-", "")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Download failed");
    } finally { setBusy(false); }
  };

  const sendEmail = async () => {
    const list = recipients.split(",").map(s => s.trim()).filter(s => s.includes("@"));
    if (!list.length && !window.confirm("No recipients typed — the server will fall back to the project's customer + Asset Manager. Continue?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/projects/${projectId}/maintenance/reports/monthly`,
        { month, send_email: true, recipients_override: list.length ? list : undefined }
      );
      toast.success(`Report sent to ${(data.sent_to || []).length} recipient(s)`);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    } finally { setBusy(false); }
  };

  const saveCfg = async () => {
    const list = recipients.split(",").map(s => s.trim()).filter(Boolean);
    setSavingCfg(true);
    try {
      await api.patch(`/projects/${projectId}/maintenance/config`, {
        report_active: reportActive,
        report_recipients: list,
      });
      toast.success("Auto-monthly settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSavingCfg(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="maint-report-modal">
      <div className="modal-card p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="label">Generate</div>
            <div className="font-display text-xl font-semibold mt-1">Monthly Ops Report</div>
          </div>
          <button type="button" onClick={onClose} className="muted" data-testid="maint-report-close"><X size={18} /></button>
        </div>

        <div className="space-y-4">
          <label className="block">
            <span className="label block mb-1">Month</span>
            <input type="month" className="input-swiss" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="maint-report-month" />
          </label>

          <label className="block">
            <span className="label block mb-1">Recipients (comma-separated emails)</span>
            <textarea className="input-swiss min-h-[70px]" placeholder="finance@customer.com, ops@customer.com"
                      value={recipients} onChange={(e) => setRecipients(e.target.value)} data-testid="maint-report-recipients" />
            <div className="text-[11px] muted mt-1">
              Leave blank to fall back to the project&apos;s customer email + Asset Manager.
            </div>
          </label>

          <div className="grid grid-cols-2 gap-2">
            <button type="button" className="btn-ghost" onClick={download} disabled={busy} data-testid="maint-report-download">
              {busy ? <Loader2 size={12} className="animate-spin inline mr-1" /> : <Download size={13} />}
              Download PDF
            </button>
            <button type="button" className="btn-accent" onClick={sendEmail} disabled={busy} data-testid="maint-report-send">
              {busy ? <Loader2 size={12} className="animate-spin inline mr-1" /> : <Mail size={13} />}
              Email to recipients
            </button>
          </div>
        </div>

        <div className="mt-6 pt-5 border-t border-[var(--line)]">
          <div className="label mb-2">Automate monthly delivery</div>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input type="checkbox" checked={reportActive} onChange={(e) => setReportActive(e.target.checked)} data-testid="maint-report-auto" />
            Send this report automatically on the 1st of each month
          </label>
          <div className="text-[11px] muted mt-1">Uses the recipient list above. Idempotent — never duplicates within a month.</div>
          <button type="button" className="btn-ghost mt-3 text-xs" onClick={saveCfg} disabled={savingCfg} data-testid="maint-report-save-cfg">
            {savingCfg ? <Loader2 size={12} className="animate-spin inline mr-1" /> : null}
            Save auto settings
          </button>
        </div>
      </div>
    </div>
  );
}



// ============================================================
// Photo annotation modal — pen / circle / rect / arrow
// ============================================================
function AnnotationModal({ photo, projectId, visitId, onClose, onSaved }) {
  const canvasRef = React.useRef(null);
  const imgRef = React.useRef(null);
  const [tool, setTool] = React.useState("pen");    // pen | circle | rect | arrow
  const [color, setColor] = React.useState("#DC2626");
  const [strokes, setStrokes] = React.useState([]);   // history of shapes
  const [current, setCurrent] = React.useState(null); // in-progress shape
  const [busy, setBusy] = React.useState(false);
  const [ready, setReady] = React.useState(false);

  const COLORS = [
    { key: "#DC2626", label: "Red" },
    { key: "#EAB308", label: "Yellow" },
    { key: "#16A34A", label: "Green" },
    { key: "#2563EB", label: "Blue" },
  ];
  const TOOLS = [
    { key: "pen", label: "Pen" },
    { key: "circle", label: "Circle" },
    { key: "rect", label: "Rect" },
    { key: "arrow", label: "Arrow" },
    { key: "label", label: "1·2·3" },
  ];

  // Draw everything to the canvas whenever strokes/current changes.
  React.useEffect(() => {
    const cv = canvasRef.current;
    const im = imgRef.current;
    if (!cv || !im || !ready) return;
    const ctx = cv.getContext("2d");
    ctx.clearRect(0, 0, cv.width, cv.height);
    ctx.drawImage(im, 0, 0, cv.width, cv.height);
    const all = current ? [...strokes, current] : strokes;
    for (const s of all) drawShape(ctx, s);
  }, [strokes, current, ready]);

  const drawShape = (ctx, s) => {
    ctx.strokeStyle = s.color;
    ctx.fillStyle = s.color;
    ctx.lineWidth = 4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    if (s.type === "pen" && s.points?.length > 1) {
      ctx.beginPath();
      ctx.moveTo(s.points[0].x, s.points[0].y);
      for (const p of s.points.slice(1)) ctx.lineTo(p.x, p.y);
      ctx.stroke();
    } else if (s.type === "circle") {
      const cx = (s.x1 + s.x2) / 2, cy = (s.y1 + s.y2) / 2;
      const rx = Math.abs(s.x2 - s.x1) / 2, ry = Math.abs(s.y2 - s.y1) / 2;
      ctx.beginPath();
      ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
      ctx.stroke();
    } else if (s.type === "rect") {
      ctx.strokeRect(s.x1, s.y1, s.x2 - s.x1, s.y2 - s.y1);
    } else if (s.type === "arrow") {
      const dx = s.x2 - s.x1, dy = s.y2 - s.y1;
      const angle = Math.atan2(dy, dx);
      const head = 14;
      ctx.beginPath();
      ctx.moveTo(s.x1, s.y1); ctx.lineTo(s.x2, s.y2); ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(s.x2, s.y2);
      ctx.lineTo(s.x2 - head * Math.cos(angle - Math.PI / 6), s.y2 - head * Math.sin(angle - Math.PI / 6));
      ctx.lineTo(s.x2 - head * Math.cos(angle + Math.PI / 6), s.y2 - head * Math.sin(angle + Math.PI / 6));
      ctx.closePath(); ctx.fill();
    } else if (s.type === "label") {
      // Numbered marker — filled circle + white bold number for easy
      // cross-reference in the visit notes / findings.
      const r = 18;
      ctx.beginPath();
      ctx.arc(s.x, s.y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#FFFFFF";
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = "#FFFFFF";
      ctx.font = "bold 22px Helvetica, Arial, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(String(s.n), s.x, s.y + 1);
    }
  };

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const onDown = (e) => {
    e.preventDefault();
    const p = getPos(e);
    if (tool === "pen") setCurrent({ type: "pen", color, points: [p] });
    else if (tool === "label") {
      // Numbered label — drops immediately on click, no drag needed.
      const nextN = strokes.filter(s => s.type === "label").length + 1;
      setStrokes([...strokes, { type: "label", color, x: p.x, y: p.y, n: nextN }]);
    }
    else setCurrent({ type: tool, color, x1: p.x, y1: p.y, x2: p.x, y2: p.y });
  };
  const onMove = (e) => {
    if (!current) return;
    const p = getPos(e);
    if (current.type === "pen") setCurrent({ ...current, points: [...current.points, p] });
    else setCurrent({ ...current, x2: p.x, y2: p.y });
  };
  const onUp = () => {
    if (current) { setStrokes([...strokes, current]); setCurrent(null); }
  };

  const undo = () => setStrokes(strokes.slice(0, -1));
  const clear = () => setStrokes([]);

  const onImageLoad = () => {
    const cv = canvasRef.current;
    const im = imgRef.current;
    if (!cv || !im) return;
    cv.width = im.naturalWidth;
    cv.height = im.naturalHeight;
    setReady(true);
  };

  const save = async () => {
    if (strokes.length === 0) {
      toast.info("Add at least one annotation before saving.");
      return;
    }
    setBusy(true);
    try {
      const cv = canvasRef.current;
      const blob = await new Promise((resolve) => cv.toBlob(resolve, "image/jpeg", 0.9));
      if (!blob) throw new Error("Failed to export canvas");
      const fd = new FormData();
      const filename = (photo.name || "photo").replace(/(\.[^.]+)?$/, "-annotated.jpg");
      fd.append("file", blob, filename);
      fd.append("project_id", projectId);
      const { data: up } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data: newPhoto } = await api.post(
        `/projects/${projectId}/maintenance/visits/${visitId}/photos`,
        {
          file_id: up.id, name: filename,
          finding_key: photo.finding_key || null,
          caption: photo.caption || null,
        }
      );
      // Tag the metadata so the tile shows "annotated" chip + hides re-annotate.
      // Simple client-side flag; server keeps original + new file independently.
      newPhoto.annotated_from = photo.file_id;
      toast.success("Annotated photo saved");
      onSaved(newPhoto);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="maint-annotate-modal">
      <div className="modal-card p-0 max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[var(--line)] flex items-center justify-between">
          <div>
            <div className="label">Annotate</div>
            <div className="font-display text-lg font-semibold mt-1 truncate max-w-md">{photo.name}</div>
          </div>
          <button type="button" onClick={onClose} className="muted" data-testid="maint-annotate-close"><X size={18} /></button>
        </div>

        {/* Toolbar */}
        <div className="px-5 py-3 border-b border-[var(--line)] bg-[var(--bg-2)] flex flex-wrap gap-3 items-center">
          <div className="flex gap-1">
            {TOOLS.map(t => (
              <button key={t.key} type="button"
                onClick={() => setTool(t.key)}
                className={`px-3 py-1.5 text-xs rounded ${tool === t.key ? "bg-[var(--accent)] text-white" : "border border-[var(--line)] muted hover:border-[var(--accent)]"}`}
                data-testid={`maint-annotate-tool-${t.key}`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex gap-1 ml-2">
            {COLORS.map(c => (
              <button key={c.key} type="button"
                onClick={() => setColor(c.key)}
                className={`w-6 h-6 rounded-full border-2 ${color === c.key ? "border-[var(--ink)]" : "border-white"}`}
                style={{ backgroundColor: c.key }}
                title={c.label}
                data-testid={`maint-annotate-color-${c.label.toLowerCase()}`} />
            ))}
          </div>
          <div className="flex gap-1 ml-auto">
            <button type="button" className="btn-ghost text-xs" onClick={undo} disabled={!strokes.length} data-testid="maint-annotate-undo">Undo</button>
            <button type="button" className="btn-ghost text-xs" onClick={clear} disabled={!strokes.length} data-testid="maint-annotate-clear">Clear</button>
          </div>
        </div>

        {/* Canvas stage */}
        <div className="p-4 bg-[#0F1524] max-h-[65vh] overflow-auto flex items-center justify-center">
          <div className="relative" style={{ maxWidth: "100%" }}>
            <img
              ref={imgRef}
              src={`${API}/files/${photo.file_id}`}
              alt={photo.name}
              onLoad={onImageLoad}
              style={{ display: "none" }}
            />
            <canvas
              ref={canvasRef}
              onMouseDown={onDown} onMouseMove={onMove}
              onMouseUp={onUp} onMouseLeave={onUp}
              onTouchStart={(e) => onDown(e.touches[0])}
              onTouchMove={(e) => onMove(e.touches[0])}
              onTouchEnd={onUp}
              style={{ maxWidth: "100%", maxHeight: "60vh", cursor: "crosshair", background: "white", touchAction: "none" }}
              data-testid="maint-annotate-canvas"
            />
            {!ready && (
              <div className="text-white text-xs muted p-8"><Loader2 size={14} className="animate-spin inline mr-1" /> Loading image…</div>
            )}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-[var(--line)] flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose} data-testid="maint-annotate-cancel">Cancel</button>
          <button type="button" className="btn-accent" disabled={busy || strokes.length === 0} onClick={save} data-testid="maint-annotate-save">
            {busy ? <Loader2 size={12} className="animate-spin inline mr-1" /> : null}
            Save annotated copy
          </button>
        </div>
      </div>
    </div>
  );
}

